import { useState, type MouseEvent, type ChangeEvent } from 'react';
import clsx from 'clsx';
import type { CheckboxProps } from '@/components/checkbox/checkbox.type';
import InputControl from '@/components/input-control';
import '@/components/checkbox/checkbox.css';

export default function Checkbox(props: CheckboxProps) {
  const {
    value,
    label,
    name,
    onChange,
    className = '',
    invalid = false,
    disabled = false,
    info = '',
    onFocus = () => {},
    onBlur = () => {},
  } = props;
  const [focused, setFocused] = useState<boolean>(false);
  const [hovered, setHovered] = useState<boolean>(false);

  const onLabelClick = (e: MouseEvent<HTMLLabelElement>): void => {
    if (disabled || focused) return;
    e.preventDefault();
    onChange(!value, name);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;
    e.stopPropagation();
    onChange(!value, name);
  };

  const onInputFocus = (): void => {
    if (disabled) return;
    setFocused(true);
    onFocus(name);
  };

  const onInputBlur = (): void => {
    if (disabled) return;
    setFocused(false);
    onBlur(name);
  };

  return (
    <label
      data-testid='checkbox'
      htmlFor={name}
      className={clsx('checkbox', className, { 'checkbox--error': invalid })}
      onClick={onLabelClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='checkbox__content'>
        <input
          data-testid='checkbox-input'
          checked={value}
          disabled={disabled}
          id={name}
          name={name}
          value={String(value)}
          type='checkbox'
          className='checkbox__input'
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onChange={onInputChange}
        />
        <div className='checkbox__control'>
          <InputControl
            shape='checkbox'
            checked={value}
            hovered={hovered}
            error={invalid}
            focused={focused}
            disabled={disabled}
          />
        </div>
        {typeof label === 'string' ? (
          <div className='checkbox__label' dangerouslySetInnerHTML={{ __html: label }} />
        ) : (
          <div className='checkbox__label'>{label()}</div>
        )}
      </div>
      {!!info && (
        <div data-testid='checkbox-info' className='checkbox__info' dangerouslySetInnerHTML={{ __html: info }} />
      )}
    </label>
  );
}

Checkbox.displayName = 'Checkbox';
